import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId

export default {
  namespaced: true,
  state: {
    outputPresets: [],
    fetchingOutputPresets: true,
    fetchingOutputPreset: true,
    outputPresetsResultInfo: {},
    currentPage: 1,
    outputPreset: null,
    creatingOutputPreset: false,
    presetTypeOptions: [],
    editingOutputPreset: false,
    emptyOutputPreset: null,
    fetchingEmptyOutputPreset: false,
    outputPresetFilters: null,
    fetchingOutputPresetFilters: false,
    deletingOutputPreset: false,
    duplicatingOutputPreset: false,
  },
  getters: {
    outputPresets: state => state.outputPresets,
    outputPreset: state => state.outputPreset,
    fetchingOutputPreset: state => state.fetchingOutputPreset,
    fetchingEmptyOutputPreset: state => state.fetchingEmptyOutputPreset,
    projectId: () => store.state.app.projectId,
    currentPage: state => state.currentPage,
  },
  mutations: {
    // Output Presets
    SET_OUTPUT_PRESETS(state, res) {
      state.outputPresets = res.result
      state.outputPresetsResultInfo = res.resultInfo
    },
    SET_FETCHING_OUTPUT_PRESETS(state, value) {
      state.fetchingOutputPresets = value
    },
    REMOVE_OUTPUT_PRESET(state, id) {
      state.outputPresets = state.outputPresets.filter(op => op.id !== id)
    },
    DELETING_OUTPUT_PRESET(state, value) {
      state.deletingOutputPreset = value
    },
    DUPLICATING_OUTPUT_PRESET(state, value) {
      state.duplicatingOutputPreset = value
    },
    // Output Preset Enums
    SET_OUTPUT_PRESET(state, value) {
      state.outputPreset = value
    },
    SET_FETCHING_OUTPUT_PRESET(state, value) {
      state.fetchingOutputPreset = value
    },
    SET_CREATING_OUTPUT_PRESET(state, value) {
      state.creatingOutputPreset = value
    },
    SET_EDITING_OUTPUT_PRESET(state, value) {
      state.editingOutputPreset = value
    },
    SET_EMPTY_OUTPUT_PRESET(state, value) {
      state.emptyOutputPreset = value
    },
    SET_FETCHING_EMPTY_OUTPUT_PRESET(state, value) {
      state.fetchingEmptyOutputPreset = value
    },
    SET_VIDEO_DESCRIPTION(state, value) {
      state.outputPreset.videoDescription = value
    },
    SET_AUDIO_DESCRIPTION(state, value) {
      state.outputPreset.audioDescription = value
    },
    SET_OUTPUT_PRESET_FILTERS(state, value) {
      state.outputPresetFilters = value
    },
    SET_FETCHING_OUTPUT_PRESET_FILTERS(state, value) {
      state.fetchingOutputPresetFilters = value
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
  },
  actions: {
    fetchOutputPresets({ commit }, payload) {
      const pageIndex = `pageIndex=${payload.pageIndex || 0}`
      const type = `type=${payload.type}`
      const category = `${
        payload.category ? `&category=${payload.category}` : ''
      }`
      const search = `${payload.search ? `&search=${payload.search}` : ''}`
      const outputGroupType = payload.outputGroupType !== undefined
        && payload.outputGroupType !== null
        ? `&outputGroupType=${payload.outputGroupType}`
        : ''
      const query = `?pageSize=10&${pageIndex}&${type}${category}${search}${outputGroupType}`

      commit('SET_FETCHING_OUTPUT_PRESETS', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/presets${query}`)
          .then(response => {
            commit('SET_OUTPUT_PRESETS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_OUTPUT_PRESETS', false)
          })
      })
    },
    deleteOutputPreset({ commit }, payload) {
      commit('DELETING_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .delete(`projects/${getProjectId()}/presets/${payload}`)
          .then(response => {
            commit('REMOVE_OUTPUT_PRESET', payload)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('DELETING_OUTPUT_PRESET', false)
          })
      })
    },
    duplicateOutputPreset({ commit }, payload) {
      commit('DUPLICATING_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .post(`projects/${getProjectId()}/presets/${payload}/duplicate`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('DUPLICATING_OUTPUT_PRESET', false)
          })
      })
    },
    getOutputPresetById({ commit }, id) {
      commit('SET_FETCHING_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/presets/${id}`)
          .then(response => {
            commit('SET_OUTPUT_PRESET', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_OUTPUT_PRESET', false)
          })
      })
    },
    // eslint-disable-next-line no-empty-pattern
    getOutputPresetByIdNoCommit({}, id) {
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/presets/${id}`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    getEmptyOutputPreset({ commit }) {
      commit('SET_FETCHING_EMPTY_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/presets/0`)
          .then(response => {
            commit('SET_EMPTY_OUTPUT_PRESET', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_FETCHING_EMPTY_OUTPUT_PRESET', false)
          })
      })
    },
    createOutputPreset({ commit }, payload) {
      commit('SET_CREATING_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .post(`projects/${getProjectId()}/presets`, payload)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            commit('SET_CREATING_OUTPUT_PRESET', false)
          })
      })
    },
    editOutputPreset({ commit }, payload) {
      commit('SET_EDITING_OUTPUT_PRESET', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .put(`projects/${getProjectId()}/presets`, payload)
          .then(response => {
            commit('SET_EDITING_OUTPUT_PRESET', false)
            resolve(response)
          })
          .catch(error => {
            commit('SET_EDITING_OUTPUT_PRESET', false)
            reject(error)
          })
      })
    },
    removeVideoDescription({ commit }) {
      commit('SET_VIDEO_DESCRIPTION', null)
    },
    removeAudioDescription({ commit }) {
      commit('SET_AUDIO_DESCRIPTION', null)
    },
    fetchOutputPresetFilters({ commit }) {
      commit('SET_FETCHING_OUTPUT_PRESET_FILTERS', true)
      return new Promise((resolve, reject) => {
        api('encoder')
          .get(`projects/${getProjectId()}/presets/filters`)
          .then(response => {
            commit('SET_OUTPUT_PRESET_FILTERS', response.data)
            commit('SET_FETCHING_OUTPUT_PRESET_FILTERS', false)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
  },
}
